/**
 * Multi-account Remember Me for sessionStorage-based auth.
 * Active auth lives in sessionStorage; localStorage stores per-user backups
 * keyed by user id so multiple accounts can stay remembered across restarts.
 */

import { hasOtherLiveTabs, touchTabHeartbeat } from './tabSession';

const ACCOUNTS_KEY = 'jetscanner_remember_me_accounts';
const LAST_USER_ID_KEY = 'jetscanner_remember_me_last_user_id';
const LEGACY_ENABLED_KEY = 'jetscanner_remember_me_enabled';
const LEGACY_AUTH_KEY = 'jetscanner_remember_me_auth';
export const TWOFA_REMEMBER_ME_KEY = 'twofa_remember_me';

const MAX_REMEMBERED_ACCOUNTS = 5;

const loadAccounts = () => {
  try {
    const raw = localStorage.getItem(ACCOUNTS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const saveAccounts = (accounts) => {
  localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
};

const getLastActiveUserId = () => localStorage.getItem(LAST_USER_ID_KEY);

const setLastActiveUserId = (userId) => {
  if (userId != null && userId !== '') {
    localStorage.setItem(LAST_USER_ID_KEY, String(userId));
  }
};

const migrateLegacyRememberMe = () => {
  const legacyAuth = localStorage.getItem(LEGACY_AUTH_KEY);
  if (!legacyAuth) {
    return;
  }

  try {
    const legacyEnabled = localStorage.getItem(LEGACY_ENABLED_KEY) === 'true';
    if (legacyEnabled) {
      const { token, refreshToken, user } = JSON.parse(legacyAuth);
      if (token && user?.id) {
        const accounts = loadAccounts();
        const userId = String(user.id);
        accounts[userId] = {
          token,
          refreshToken: refreshToken || null,
          user,
          lastUsedAt: Date.now(),
        };
        saveAccounts(accounts);
        setLastActiveUserId(userId);
      }
    }
  } catch {
    // ignore corrupt legacy data
  }

  localStorage.removeItem(LEGACY_ENABLED_KEY);
  localStorage.removeItem(LEGACY_AUTH_KEY);
};

migrateLegacyRememberMe();

const normalizeUser = (user) =>
  typeof user === 'string' ? JSON.parse(user) : user;

const pruneAccounts = (accounts) => {
  const entries = Object.entries(accounts);
  if (entries.length <= MAX_REMEMBERED_ACCOUNTS) {
    return accounts;
  }

  const sorted = entries.sort(
    (a, b) => (b[1].lastUsedAt || 0) - (a[1].lastUsedAt || 0)
  );
  return Object.fromEntries(sorted.slice(0, MAX_REMEMBERED_ACCOUNTS));
};

export const hasRememberedAccounts = () =>
  Object.keys(loadAccounts()).length > 0;

/** @deprecated Use hasRememberedAccounts() or isUserRemembered(userId) */
export const isRememberMeEnabled = () => hasRememberedAccounts();

export const isUserRemembered = (userId) => {
  if (userId == null) {
    return false;
  }
  return Boolean(loadAccounts()[String(userId)]);
};

export const getRememberedAccounts = () => {
  const accounts = loadAccounts();
  return Object.entries(accounts)
    .map(([userId, entry]) => ({
      userId,
      token: entry.token,
      refreshToken: entry.refreshToken,
      user: entry.user,
      lastUsedAt: entry.lastUsedAt || 0,
    }))
    .sort((a, b) => b.lastUsedAt - a.lastUsedAt);
};

export const persistRememberMeAuth = ({ token, refreshToken, user }) => {
  if (!token || !user?.id) {
    return;
  }

  const userId = String(user.id);
  const accounts = loadAccounts();
  accounts[userId] = {
    token,
    refreshToken: refreshToken || null,
    user: normalizeUser(user),
    lastUsedAt: Date.now(),
  };

  saveAccounts(pruneAccounts(accounts));
  setLastActiveUserId(userId);
};

export const removeRememberedAccount = (userId) => {
  const id = String(userId);
  const accounts = loadAccounts();
  delete accounts[id];
  saveAccounts(accounts);

  if (getLastActiveUserId() === id) {
    const remaining = Object.keys(accounts);
    if (remaining.length > 0) {
      const mostRecent = getRememberedAccounts()[0];
      setLastActiveUserId(mostRecent?.userId ?? null);
    } else {
      localStorage.removeItem(LAST_USER_ID_KEY);
    }
  }
};

export const clearRememberMeAuthForUser = (userId) => {
  if (userId != null && userId !== '' && userId !== 'guest') {
    removeRememberedAccount(userId);
  }
};

export const clearRememberMeAuth = () => {
  localStorage.removeItem(ACCOUNTS_KEY);
  localStorage.removeItem(LAST_USER_ID_KEY);
};

export const handleRememberMeOnLogin = (rememberMe, { token, refreshToken, user }) => {
  const parsedUser = normalizeUser(user);
  if (rememberMe) {
    persistRememberMeAuth({ token, refreshToken, user: parsedUser });
  } else {
    clearRememberMeAuthForUser(parsedUser?.id);
  }
};

/** Keep localStorage backup in sync when session auth changes for a remembered user. */
export const syncRememberMeAuthFromSession = () => {
  const token = sessionStorage.getItem('token');
  const refreshToken = sessionStorage.getItem('refreshToken');
  const userRaw = sessionStorage.getItem('user');

  if (!token || !userRaw) {
    return;
  }

  try {
    const user = JSON.parse(userRaw);
    if (!isUserRemembered(user.id)) {
      return;
    }
    persistRememberMeAuth({ token, refreshToken, user });
  } catch {
    // session user parse failed — do not clear other remembered accounts
  }
};

const writeAuthToSessionStorage = ({ token, refreshToken, user }) => {
  sessionStorage.setItem('token', token);
  if (refreshToken) {
    sessionStorage.setItem('refreshToken', refreshToken);
  }
  sessionStorage.setItem(
    'user',
    typeof user === 'string' ? user : JSON.stringify(user)
  );
};

/**
 * Restore a specific remembered account into sessionStorage.
 * @returns {{ token, refreshToken, user } | null}
 */
export const restoreRememberMeAccountToSession = (userId) => {
  const id = String(userId);
  const entry = loadAccounts()[id];
  if (!entry?.token || !entry?.user) {
    return null;
  }

  writeAuthToSessionStorage(entry);
  setLastActiveUserId(id);
  entry.lastUsedAt = Date.now();
  const accounts = loadAccounts();
  accounts[id] = entry;
  saveAccounts(accounts);

  return {
    token: entry.token,
    refreshToken: entry.refreshToken,
    user: entry.user,
  };
};

/**
 * Hydrate sessionStorage on app load when the tab has no active session.
 * Uses the most recently used remembered account.
 * Skips auto-restore when another tab is already open (new tab should stay logged out).
 */
export const restoreRememberMeAuthToSession = () => {
  if (sessionStorage.getItem('token') && sessionStorage.getItem('user')) {
    touchTabHeartbeat();
    return false;
  }

  if (hasOtherLiveTabs()) {
    return false;
  }

  const accounts = getRememberedAccounts();
  if (accounts.length === 0) {
    return false;
  }

  const targetId = getLastActiveUserId() || accounts[0].userId;
  const restored = restoreRememberMeAccountToSession(targetId);
  if (restored) {
    touchTabHeartbeat();
  }
  return Boolean(restored);
};

export const applyPendingTwoFactorRememberMe = ({ token, refreshToken, user }) => {
  const pending = sessionStorage.getItem(TWOFA_REMEMBER_ME_KEY);
  if (pending === null) {
    return;
  }

  handleRememberMeOnLogin(pending === 'true', { token, refreshToken, user });
  sessionStorage.removeItem(TWOFA_REMEMBER_ME_KEY);
};
